import React, { useEffect, useRef, useState } from "react";
import { BellRing, Copy, PlayCircle, RefreshCw, Save, Volume2, Upload, Trash2, CheckCircle2, XCircle, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { SectionBlock, Field, FieldGrid } from "./Channel";
import { useUnsaved } from "../DashboardShell";

const TYPES = [
  { key: "follow", label: "New Follower", color: "cyan"   },
  { key: "sub",    label: "New Subscriber", color: "pink" },
  { key: "tip",    label: "Tips",           color: "volt" },
  { key: "raid",   label: "Raids",          color: "orange" },
];

/**
 * Alerts — per-type config + OBS browser source URL + test trigger.
 * The overlay page itself is served at /alert-overlay?u=<user>&t=<token>
 * (frontend-only render — WebSocket connects to /api/alerts/ws for events).
 */
export default function Alerts() {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [initial, setInitial] = useState(null);
  const [busy, setBusy] = useState(false);
  const [customSounds, setCustomSounds] = useState([]);

  const load = () => api.get("/alerts/settings").then((r) => {
    setState(r.data);
    setInitial({ config: JSON.parse(JSON.stringify(r.data.config)) });
  });
  const loadSounds = () => api.get("/alerts/sounds/mine").then((r) => setCustomSounds(r.data.sounds || []));
  useEffect(() => { load(); loadSounds(); }, []);

  const dirty = initial && state && JSON.stringify(state.config) !== JSON.stringify(initial.config);
  useUnsaved(dirty);

  if (!state) return <div className="text-sm text-zinc-500">Loading…</div>;

  const updateType = (key, patch) => {
    setState({ ...state, config: { ...state.config, [key]: { ...state.config[key], ...patch } } });
  };

  const save = async () => {
    setBusy(true);
    try {
      const patch = {};
      TYPES.forEach((t) => { patch[t.key] = state.config[t.key]; });
      const { data } = await api.patch("/alerts/settings", patch);
      setState({ ...state, config: data.config });
      setInitial({ config: JSON.parse(JSON.stringify(data.config)) });
      toast.success("Alert settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const rotate = async () => {
    if (!window.confirm("Rotate overlay token? Your current OBS browser source will stop working.")) return;
    const { data } = await api.post("/alerts/rotate_token");
    setState({ ...state, overlay_token: data.overlay_token });
    toast.success("New overlay token generated");
  };

  const fireTest = async (type) => {
    try {
      await api.post("/alerts/test", { type, username: "TestUser", amount: 500, count: 42 });
      toast.success(`Test ${type} alert fired`);
    } catch (e) {
      toast.error("Failed to fire test alert");
    }
  };

  const overlayUsername = state.username || user?.username || "";
  const overlayUrl = `${window.location.origin}/alert-overlay?u=${encodeURIComponent(overlayUsername)}&t=${state.overlay_token}`;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="section-alerts">
      {/* Overlay browser source */}
      <SectionBlock icon={BellRing} title="OBS Browser Source" color="volt">
        <p className="text-xs text-zinc-400 -mt-1">
          Add this URL as a Browser Source in OBS (1920×1080, transparent). Alerts fire in real-time as followers,
          subscribers, and tips come in.
        </p>
        <div className="flex gap-2 flex-wrap">
          <input
            className="sc-input flex-1 min-w-[280px] sc-mono text-xs"
            value={overlayUrl}
            readOnly
            data-testid="alerts-overlay-url"
          />
          <button
            onClick={() => { navigator.clipboard.writeText(overlayUrl); toast.success("Copied"); }}
            className="sc-btn text-xs flex items-center gap-1.5"
            data-testid="alerts-copy-url"
          >
            <Copy className="w-3.5 h-3.5" /> Copy
          </button>
          <button
            onClick={rotate}
            className="sc-btn-ghost text-xs flex items-center gap-1.5"
            data-testid="alerts-rotate-token"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Rotate token
          </button>
        </div>
      </SectionBlock>

      {/* Custom sound uploads */}
      <CustomSoundsCard
        sounds={customSounds}
        onUploaded={loadSounds}
        onDeleted={loadSounds}
      />

      {/* Per-type config */}
      {TYPES.map((t) => {
        const cfg = state.config[t.key] || {};
        return (
          <SectionBlock key={t.key} icon={BellRing} title={t.label} color={t.color}>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer text-sm text-zinc-300">
                <input
                  type="checkbox"
                  checked={!!cfg.enabled}
                  onChange={(e) => updateType(t.key, { enabled: e.target.checked })}
                  data-testid={`alerts-${t.key}-enabled`}
                />
                Enabled
              </label>
              <button
                onClick={() => fireTest(t.key)}
                disabled={!cfg.enabled}
                className="sc-btn-ghost text-xs flex items-center gap-1.5 disabled:opacity-40"
                data-testid={`alerts-${t.key}-test`}
              >
                <PlayCircle className="w-3.5 h-3.5" /> Test
              </button>
            </div>
            <FieldGrid>
              <Field label="Sound">
                <select
                  className="sc-input"
                  value={cfg.sound || "chime"}
                  onChange={(e) => updateType(t.key, { sound: e.target.value })}
                  data-testid={`alerts-${t.key}-sound`}
                >
                  <optgroup label="Built-in">
                    {(state.sounds || []).map((s) => <option key={s} value={s}>{s}</option>)}
                  </optgroup>
                  {customSounds.filter((s) => s.status === "approved").length > 0 && (
                    <optgroup label="Your custom sounds">
                      {customSounds.filter((s) => s.status === "approved").map((s) => (
                        <option key={s.key} value={s.key}>{s.display_name}</option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </Field>
              <Field label="Animation">
                <select
                  className="sc-input"
                  value={cfg.animation || "pop"}
                  onChange={(e) => updateType(t.key, { animation: e.target.value })}
                  data-testid={`alerts-${t.key}-animation`}
                >
                  {(state.animations || []).map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </Field>
              <Field label="Duration (ms)" hint="1000–15000">
                <input
                  type="number" min="1000" max="15000" step="500"
                  className="sc-input"
                  value={cfg.duration_ms || 5000}
                  onChange={(e) => updateType(t.key, { duration_ms: parseInt(e.target.value || "5000", 10) })}
                  data-testid={`alerts-${t.key}-duration`}
                />
              </Field>
              {t.key === "tip" && (
                <Field label="Minimum tip (GC)" hint="Alerts fire only for tips ≥ this">
                  <input
                    type="number" min="0"
                    className="sc-input"
                    value={cfg.min_amount_gc ?? 10}
                    onChange={(e) => updateType(t.key, { min_amount_gc: parseInt(e.target.value || "0", 10) })}
                    data-testid={`alerts-tip-min-amount`}
                  />
                </Field>
              )}
              <Field label="Message template" hint="Use {username}, {amount}, {count}" fullWidth>
                <input
                  className="sc-input"
                  value={cfg.message_template || ""}
                  onChange={(e) => updateType(t.key, { message_template: e.target.value })}
                  data-testid={`alerts-${t.key}-template`}
                />
              </Field>
            </FieldGrid>
          </SectionBlock>
        );
      })}

      {/* Sticky save */}
      {dirty && (
        <div
          className="sticky bottom-4 z-20 flex items-center justify-between p-3 rounded border border-[color:var(--sc-volt)] bg-[#0F0F14]/95 backdrop-blur"
          data-testid="alerts-save-bar"
        >
          <span className="text-xs text-zinc-300 flex items-center gap-2">
            <Volume2 className="w-3.5 h-3.5 text-[color:var(--sc-orange)]" />
            Unsaved changes
          </span>
          <button
            onClick={save}
            disabled={busy}
            className="sc-btn text-xs flex items-center gap-1.5 disabled:opacity-50"
            data-testid="alerts-save-btn"
          >
            <Save className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Save alert settings"}
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CustomSoundsCard — upload + review + delete streamer's own alert sounds.
// Sounds go through an admin approval workflow before they appear in the
// sound picker (same pattern as custom emotes).
// ---------------------------------------------------------------------------
const STATUS_META = {
  pending:  { label: "Pending review", color: "orange", Icon: Clock },
  approved: { label: "Approved",       color: "volt",   Icon: CheckCircle2 },
  rejected: { label: "Rejected",       color: "pink",   Icon: XCircle },
};

function CustomSoundsCard({ sounds, onUploaded, onDeleted }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [uploading, setUploading] = useState(false);

  const chooseFile = (f) => {
    if (!f) return;
    if (f.size > 200 * 1024) return toast.error("File must be ≤ 200 KB");
    if (!/^audio\/(mpeg|mp3|wav|x-wav|ogg|webm)$/.test(f.type)) return toast.error("MP3, WAV, OGG, or WEBM only");
    setFile(f);
  };

  const upload = async () => {
    if (!file) return toast.error("Choose an audio file");
    if (!name.trim() || name.trim().length < 2) return toast.error("Name must be at least 2 characters");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("name", name.trim());
    setUploading(true);
    try {
      await api.post("/alerts/sounds/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Sound uploaded — waiting for review");
      setFile(null); setName("");
      if (fileRef.current) fileRef.current.value = "";
      onUploaded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const preview = (url) => {
    try { new Audio(url).play().catch(() => {}); } catch { /* ignore */ }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this sound?")) return;
    try { await api.delete(`/alerts/sounds/${id}`); toast.success("Deleted"); onDeleted?.(); }
    catch { toast.error("Failed to delete"); }
  };

  return (
    <SectionBlock icon={Volume2} title="Custom Sounds" color="pink">
      <p className="text-xs text-zinc-400 -mt-1">
        Upload your own short alert sounds (MP3 / WAV / OGG / WEBM · ≤ 200 KB · ideally ≤ 3 seconds). Approved sounds
        appear in the sound picker above.
      </p>
      <div className="grid md:grid-cols-[1fr_auto] gap-3 items-end">
        <FieldGrid>
          <Field label="Display name">
            <input className="sc-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Airhorn Blast" maxLength={32} data-testid="alerts-sound-name" />
          </Field>
          <Field label="Audio file">
            <input
              ref={fileRef}
              type="file"
              accept="audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/webm"
              onChange={(e) => chooseFile(e.target.files?.[0])}
              className="sc-input file:mr-3 file:px-2 file:py-1 file:rounded file:border-0 file:bg-white/10 file:text-white text-xs"
              data-testid="alerts-sound-file"
            />
          </Field>
        </FieldGrid>
        <button
          onClick={upload}
          disabled={uploading || !file || name.trim().length < 2}
          className="sc-btn text-sm flex items-center gap-2 disabled:opacity-50 h-[42px]"
          data-testid="alerts-sound-upload-btn"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          Submit
        </button>
      </div>

      {sounds.length > 0 && (
        <div className="mt-4 space-y-1.5" data-testid="alerts-sounds-list">
          {sounds.map((s) => {
            const meta = STATUS_META[s.status] || STATUS_META.pending;
            const Icon = meta.Icon;
            return (
              <div key={s.id} className="flex items-center gap-3 py-2 px-3 border border-white/10 rounded" data-testid={`alerts-sound-${s.id}`}>
                <button
                  onClick={() => preview(s.url)}
                  className="p-1 rounded hover:bg-white/5 text-zinc-400 hover:text-[color:var(--sc-cyan)]"
                  title="Preview"
                  data-testid={`alerts-sound-preview-${s.id}`}
                >
                  <PlayCircle className="w-4 h-4" />
                </button>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold text-zinc-100 truncate">{s.display_name}</div>
                  <div className="text-[10px] sc-mono text-zinc-500">{(s.size / 1024).toFixed(1)} KB · {s.content_type}</div>
                </div>
                <span className="flex items-center gap-1 text-[10px] sc-mono" style={{ color: `var(--sc-${meta.color})` }}>
                  <Icon className="w-3 h-3" /> {meta.label}
                </span>
                <button
                  onClick={() => remove(s.id)}
                  className="p-1 rounded hover:bg-white/5 text-zinc-500 hover:text-[color:var(--sc-pink)]"
                  data-testid={`alerts-sound-delete-${s.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </SectionBlock>
  );
}

